// NEW_FILE_JUSTIFIED: Single section render + nested field dnd in builder canvas
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { GripVertical, Trash2 } from 'lucide-react';
import type { ISectionCardProps } from '@/@types/insuranceBuilder';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { FieldCard } from './FieldCard';

export function SectionCard({
  section,
  isSelected,
  selection,
  dispatch,
  isAiExtracted,
  aiFieldKeys,
  density = 'cozy',
}: ISectionCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.sectionKey,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function handleFieldDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = section.fields.findIndex(f => f.fieldKey === active.id);
    const newIndex = section.fields.findIndex(f => f.fieldKey === over.id);
    const reordered = [...section.fields];
    const [moved] = reordered.splice(oldIndex, 1);
    if (moved) reordered.splice(newIndex, 0, moved);
    dispatch({
      type: 'REORDER_FIELDS',
      sectionKey: section.sectionKey,
      orderedKeys: reordered.map(f => f.fieldKey),
    });
  }
  function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    dispatch({ type: 'DELETE_SECTION', sectionKey: section.sectionKey });
  }
  function handleSelectSection(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('[data-no-select]')) return; // AS_JUSTIFIED: e.target is Element here; closest() requires Element
    dispatch({ type: 'SELECT', selection: { kind: 'section', sectionKey: section.sectionKey } });
  }

  const gridClass =
    section.columns === 3
      ? 'grid grid-cols-3 gap-3'
      : section.columns === 2
        ? 'grid grid-cols-2 gap-3'
        : 'space-y-3';

  return (
    <Card
      ref={setNodeRef}
      style={style}
      onClick={handleSelectSection}
      className={cn(
        'cursor-pointer transition-all',
        density === 'compact' ? 'p-3' : 'p-5',
        isSelected && 'ring-2 ring-primary ring-offset-2 ring-offset-background-secondary'
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <button
          {...attributes}
          {...listeners}
          aria-label="Drag section"
          data-no-select
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <span className="font-medium text-sm">{section.title}</span>
        <Badge variant="secondary" className="text-xs">
          {section.columns} col · {section.fields.length} field
          {section.fields.length !== 1 ? 's' : ''}
        </Badge>
        {isAiExtracted && (
          <Badge
            variant="secondary"
            className="bg-primary/15 text-primary border border-primary/30"
          >
            AI extracted
          </Badge>
        )}
        <div className="ml-auto" data-no-select>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDelete}
            aria-label={`Delete section ${section.title}`}
          >
            <Trash2 className="w-4 h-4 text-destructive" />
          </Button>
        </div>
      </div>
      {section.fields.length === 0 && (
        <p className="text-xs text-muted-foreground border border-dashed rounded p-3 text-center">
          Click a field type in the toolbox to add fields here
        </p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleFieldDragEnd}
      >
        <SortableContext items={section.fields.map(f => f.fieldKey)} strategy={rectSortingStrategy}>
          <div className={gridClass}>
            {section.fields.map(field => (
              <FieldCard
                key={field.fieldKey}
                field={field}
                sectionKey={section.sectionKey}
                isSelected={selection?.kind === 'field' && selection.fieldKey === field.fieldKey}
                dispatch={dispatch}
                isAiExtracted={aiFieldKeys?.has(field.fieldKey)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </Card>
  );
}
